import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { forumApi } from '../services/forumApi';
import HeaderHome from '../components/layout/HeaderHome';
import NavigationButton from '../components/layout/Navigation';
import UserProfileModal from '../components/modals/UserProfileModal';

const ForumMainPage = () => {
    const [threads, setThreads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [newThread, setNewThread] = useState({ title: '', content: '', image: null });
    const [mentionQuery, setMentionQuery] = useState('');
    const [mentionUsers, setMentionUsers] = useState([]);
    const [showMentionDropdown, setShowMentionDropdown] = useState(false);
    const [cursorPosition, setCursorPosition] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [filteredThreads, setFilteredThreads] = useState([]);
    const navigate = useNavigate();
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);

    const user = JSON.parse(localStorage.getItem('user'));
    const isAdmin = user?.role === 'admin' || user?.role === 'staff' || user?.is_staff || user?.is_superuser;

    useEffect(() => {
        fetchThreads();
    }, []);

    const fetchThreads = async () => {
        try {
            const res = await forumApi.getThreads();
            setThreads(res.data);
            setFilteredThreads(res.data);
            setLoading(false);
        } catch (error) {
            console.error('Failed to fetch threads:', error);
            setLoading(false);
        }
    };

    const handleDeleteThread = async (e, slug) => {
        e.preventDefault();
        e.stopPropagation();
        if (!window.confirm("Yakin ingin menghapus diskusi ini beserta seluruh balasannya?")) return;
        try {
            await forumApi.deleteThread(slug);
            fetchThreads();
        } catch (error) {
            alert('Gagal menghapus diskusi');
        }
    };

    const handleSearch = (query) => {
        setSearchQuery(query);
        if (!query) {
            setFilteredThreads(threads);
        } else {
            const filtered = threads.filter(thread => 
                thread.title.toLowerCase().includes(query.toLowerCase()) ||
                thread.content.toLowerCase().includes(query.toLowerCase())
            );
            setFilteredThreads(filtered);
        }
    };

    const handleCreateThread = async (e) => {
        e.preventDefault();
        try {
            const formData = new FormData();
            formData.append('title', newThread.title);
            formData.append('content', newThread.content);
            if (newThread.image) {
                formData.append('image', newThread.image);
            }
            
            await forumApi.createThread(formData);
            setShowForm(false);
            setNewThread({ title: '', content: '', image: null });
            fetchThreads();
            alert('Diskusi Anda berhasil dibuat! Diskusi akan ditinjau oleh Admin terlebih dahulu sebelum tayang ke publik.');
        } catch (error) {
            console.error('Failed to create thread', error);
            alert('Gagal membuat thread');
        }
    };

    const handleTextChange = async (e) => {
        const value = e.target.value;
        setNewThread({ ...newThread, content: value });

        const cursor = e.target.selectionStart;
        setCursorPosition(cursor);

        // Find if user is typing a mention word
        const textBeforeCursor = value.substring(0, cursor);
        const match = textBeforeCursor.match(/@([\w.]*)$/);

        if (match) {
            const query = match[1];
            setShowMentionDropdown(true);
            setMentionQuery(query);
            try {
                const res = await forumApi.searchUsers(query);
                setMentionUsers(res.data);
            } catch (err) { }
        } else {
            setShowMentionDropdown(false);
            setMentionUsers([]);
        }
    };

    const handleMentionSelect = (username) => {
        const textBeforeCursor = newThread.content.substring(0, cursorPosition);
        const textAfterCursor = newThread.content.substring(cursorPosition);
        const match = textBeforeCursor.match(/@([\w.]*)$/);
        if (match) {
            const newTextBefore = textBeforeCursor.substring(0, match.index) + `@${username} `;
            setNewThread({ ...newThread, content: newTextBefore + textAfterCursor });
            setShowMentionDropdown(false);
            setMentionUsers([]);
        }
    };

    const renderContentWithMentions = (text) => {
        if (!text) return null;
        const parts = text.split(/(@[\w\.]+)/g);
        return parts.map((part, index) => {
            if (part.startsWith('@')) {
                return <span key={index} className="text-blue-600 font-semibold">{part}</span>;
            }
            return <span key={index}>{part}</span>;
        });
    };

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: 'short', day: 'numeric' };
        return new Date(dateString).toLocaleDateString('id-ID', options);
    };

    return (
        <div className="min-h-screen bg-gray-50 pb-12 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Helmet>
                <title>Forum Tanya Jawab & Diskusi Syariah - Barakah Economy</title>
                <meta name="description" content="Forum diskusi dan tanya jawab Barakah Economy. Diskusikan berbagai topik seputar bisnis syariah, fiqih muamalah, dan kolaborasi ummat." />
                <meta property="og:title" content="Forum Tanya Jawab Syariah - Barakah Economy" />
                <meta property="og:description" content="Diskusikan bisnis syariah, fiqih muamalah, dan kolaborasi ummat bersama pakar dan komunitas." />
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://barakah.cloud/forum" />
                <link rel="canonical" href="https://barakah.cloud/forum" />
            </Helmet>

            <HeaderHome onSearch={handleSearch} />

            <div className="py-6 flex flex-col md:flex-row justify-between items-center mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                        <span className="w-1.5 h-6 bg-green-600 rounded-full"></span>
                        Forum Tanya Jawab
                    </h1>
                    <p className="mt-1 text-sm text-gray-500">Diskusikan topik, tanya pakar, dan berbagai pengalaman.</p>
                </div>
                <div className="mt-4 md:mt-0">
                    {user ? (
                        <button
                            onClick={() => setShowForm(!showForm)}
                            className="inline-flex items-center px-4 py-2 border border-transparent rounded-full shadow-sm text-sm font-medium text-white bg-green-700 hover:bg-green-800 focus:outline-none"
                        >
                            <span className="material-icons mr-2 text-[18px]">add</span> Buat Diskusi
                        </button>
                    ) : (
                        <button
                            onClick={() => navigate('/login')}
                            className="inline-flex items-center px-4 py-2 border border-green-700 rounded-full shadow-sm text-sm font-medium text-green-700 bg-white hover:bg-green-50 focus:outline-none"
                        >
                            Login untuk Diskusi
                        </button>
                    )}
                </div>
            </div>

            {showForm && user && (
                <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                    <h2 className="text-xl font-bold mb-4">Buat Diskusi Baru</h2>
                    <form onSubmit={handleCreateThread}>
                        <div className="mb-4">
                            <label className="block text-gray-700 text-sm font-bold mb-2">Judul</label>
                            <input
                                type="text"
                                required
                                value={newThread.title}
                                onChange={(e) => setNewThread({ ...newThread, title: e.target.value })}
                                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Judul diskusi..."
                            />
                        </div>
                        <div className="mb-4 relative">
                            <label className="block text-gray-700 text-sm font-bold mb-2">Isi Diskusi</label>
                            <textarea
                                required
                                rows="5"
                                value={newThread.content}
                                onChange={handleTextChange}
                                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Tulis pertanyaan atau diskusi Anda di sini... Ketik @ untuk mention"
                            ></textarea>

                            {/* Mention Dropdown */}
                            {showMentionDropdown && mentionUsers.length > 0 && (
                                <div className="absolute z-10 w-64 bg-white border border-gray-200 rounded-md shadow-lg -mt-1 max-h-48 overflow-y-auto">
                                    {mentionUsers.map((u) => (
                                        <div
                                            key={u.id}
                                            onClick={() => handleMentionSelect(u.username)}
                                            className="px-4 py-2 hover:bg-blue-50 cursor-pointer flex flex-col border-b last:border-0"
                                        >
                                            <span className="font-semibold text-sm text-gray-800">{u.name}</span>
                                            <span className="text-xs text-gray-500">@{u.username}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="mb-4">
                            <label className="block text-gray-700 text-sm font-bold mb-2">Unggah Gambar (Opsional, Max 5MB)</label>
                            <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (file && file.size > 5 * 1024 * 1024) {
                                        alert('Ukuran gambar maksimal 5MB');
                                        e.target.value = null;
                                        setNewThread({ ...newThread, image: null });
                                    } else {
                                        setNewThread({ ...newThread, image: file });
                                    }
                                }}
                                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                        <div className="flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => setShowForm(false)}
                                className="px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md"
                            >
                                Batal
                            </button>
                            <button
                                type="submit"
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                            >
                                Posting
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {loading ? (
                <div className="flex justify-center py-20">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
            ) : filteredThreads.length === 0 ? (
                <div className="text-center py-10">
                    {searchQuery ? (
                        <p className="text-gray-500">Tidak ditemukan hasil untuk "{searchQuery}"</p>
                    ) : (
                        <p className="text-gray-500">Belum ada diskusi yang dibuat.</p>
                    )}
                </div>
            ) : (
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                    {filteredThreads.map((thread) => (
                        <Link
                            to={`/forum/${thread.slug}`}
                            key={thread.id}
                            className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 border border-gray-100 flex flex-col h-full overflow-hidden"
                        >
                            <div className="p-4 flex flex-col flex-grow">
                                <div className="flex items-center gap-2 mb-2">
                                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-800 font-bold text-sm flex-shrink-0">
                                        {thread.author?.full_name?.charAt(0) || 'U'}
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium text-gray-900 truncate">
                                            <span 
                                                className="hover:text-blue-600 cursor-pointer"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    setSelectedUserId(thread.author?.id);
                                                    setIsProfileModalOpen(true);
                                                }}
                                            >
                                                {thread.author?.full_name}
                                            </span>
                                            {thread.author?.is_expert && (
                                                <span className="ml-1 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                                    Pakar
                                                </span>
                                            )}
                                        </p>
                                        <p className="text-xs text-gray-500">{formatDate(thread.created_at)}</p>
                                    </div>
                                </div>

                                {!thread.is_approved && (
                                    <div className="mb-2">
                                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-black bg-amber-100 text-amber-900 border border-amber-200">
                                            <span className="material-icons text-[11px]">schedule</span>
                                            Menunggu Persetujuan Admin
                                        </span>
                                    </div>
                                )}

                                <h3 className="text-md md:text-lg font-bold text-gray-900 mb-2 line-clamp-2">
                                    {thread.title}
                                </h3>

                                <p className="text-sm text-gray-600 line-clamp-3 mb-4 flex-grow">
                                    {renderContentWithMentions(thread.content)}
                                </p>
                            </div>

                            <div className="bg-gray-50 px-4 py-3 border-t border-gray-100 flex justify-between items-center text-xs text-gray-500">
                                <div className="flex gap-4">
                                    <div className="flex items-center gap-1">
                                        <span className="material-icons text-gray-400 text-[16px]">favorite</span>
                                        <span>{thread.likes_count || 0} Suka</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <span className="material-icons text-gray-400 text-[16px]">chat</span>
                                        <span>{thread.replies_count} Balasan</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <span className="material-icons text-gray-400 text-[16px]">visibility</span>
                                        <span>{thread.views} Dilihat</span>
                                    </div>
                                </div>
                                {(isAdmin || user?.id === thread.author?.id) && (
                                    <button
                                        onClick={(e) => handleDeleteThread(e, thread.slug)}
                                        className="text-red-500 hover:text-red-700 flex items-center gap-0.5 font-bold"
                                    >
                                        <span className="material-icons text-[14px]">delete</span> Hapus
                                    </button>
                                )}
                            </div>
                        </Link>
                    ))}
                </div>
            )}
            <NavigationButton />
            <UserProfileModal 
                userId={selectedUserId} 
                isOpen={isProfileModalOpen} 
                onClose={() => setIsProfileModalOpen(false)} 
            />
        </div>
    );
};

export default ForumMainPage;
